import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge, Card, Empty, ErrorBox, Kpi, Skeleton } from "@/components/ui";
import type {
  CaseManagerMessage,
  ProtocolResearchPacket,
  RPISOperationalSummary,
} from "@/types";

const ROLE_COPY: Record<string, string> = {
  admin: "Full RPIS Manager dashboard",
  physician: "Clinical review, protocol research, patient and task review",
  coordinator: "Tasks, appointments, case-manager drafts, and reminders",
  viewer: "Read-only operational view",
};

export default function RPISManager() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const summary = useQuery({
    queryKey: ["rpis-manager-summary"],
    queryFn: () => api.get<RPISOperationalSummary>("/rpis-manager/operational-summary"),
  });
  const packets = useQuery({
    queryKey: ["protocol-research-packets"],
    queryFn: () =>
      api.get<{ items: ProtocolResearchPacket[] }>("/rpis-manager/protocol-research/packets"),
  });
  const drafts = useQuery({
    queryKey: ["case-manager-drafts"],
    queryFn: () =>
      api.get<{ items: CaseManagerMessage[] }>("/rpis-manager/case-manager-drafts?limit=10"),
  });
  const runWatcher = useMutation({
    mutationFn: () => api.post("/watchers/run-status-watcher", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rpis-manager-summary"] }),
  });
  const exportReport = useMutation({
    mutationFn: () => api.get<any>("/rpis-manager/export/operational-report"),
  });

  const role = user?.role || "viewer";
  const caps = summary.data?.role_capabilities || {};
  const canWrite = role === "admin" || role === "physician" || role === "coordinator";
  const canProtocol = role === "admin" || role === "physician";

  if (summary.isLoading) return <Card><Skeleton rows={8} /></Card>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-50">RPIS Manager</h1>
          <div className="text-sm text-slate-400">
            {ROLE_COPY[role] || "Limited operational view"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link className="btn-ghost" to="/rpis-manager/due-reminders">View due reminders</Link>
          {caps.can_run_watcher ? (
            <button className="btn-ghost" onClick={() => runWatcher.mutate()} disabled={runWatcher.isPending}>
              Run status watcher
            </button>
          ) : null}
          {caps.can_export_reports ? (
            <button className="btn-primary" onClick={() => exportReport.mutate()} disabled={exportReport.isPending}>
              Export operational report
            </button>
          ) : null}
        </div>
      </div>

      {summary.error ? <ErrorBox error={summary.error} /> : null}
      {runWatcher.error ? <ErrorBox error={runWatcher.error} /> : null}
      {exportReport.error ? <ErrorBox error={exportReport.error} /> : null}

      {summary.data ? (
        <>
          <HealthStrip summary={summary.data} />
          <CounterGrid summary={summary.data} />

          <div className="grid xl:grid-cols-3 gap-4">
            <Card title="Recommended internal actions">
              <ul className="space-y-2 text-sm">
                {summary.data.recommended_internal_actions.map((item, index) => (
                  <li key={index} className="card-tight">
                    <div className="flex items-center gap-2">
                      <Badge tone={item.priority === "high" ? "bad" : item.priority === "normal" ? "info" : "muted"}>
                        {item.priority}
                      </Badge>
                      <span className="text-slate-100">{item.action}</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{item.basis}</div>
                  </li>
                ))}
              </ul>
            </Card>

            <QuickActions canWrite={canWrite} canProtocol={canProtocol} />

            <Card title="Report export">
              {exportReport.data ? (
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap text-xs text-slate-300">
                  {JSON.stringify(exportReport.data, null, 2)}
                </pre>
              ) : (
                <div className="text-sm text-slate-400">
                  JSON export includes operational summary, due reminders, watcher status, monthly statistics, and internal recommendations.
                </div>
              )}
            </Card>
          </div>

          <div className="grid xl:grid-cols-2 gap-4">
            <CaseManagerDraftPanel enabled={canWrite} drafts={drafts.data?.items || []} />
            <ProtocolResearchPanel
              enabled={canProtocol}
              packets={packets.data?.items || []}
              onCreated={() => qc.invalidateQueries({ queryKey: ["protocol-research-packets"] })}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

function HealthStrip({ summary }: { summary: RPISOperationalSummary }) {
  return (
    <Card>
      <div className="grid md:grid-cols-4 gap-3 text-sm">
        <StripItem label="Summary" value="online" tone="ok" />
        <StripItem label="Watcher" value={summary.watcher.status} tone={summary.watcher.open_watcher_alerts ? "warn" : "ok"} />
        <StripItem label="Mode" value={summary.watcher.mode.replaceAll("_", " ")} tone="info" />
        <StripItem label="External sending" value={String(summary.external_sending_performed)} tone="ok" />
      </div>
    </Card>
  );
}

function StripItem({ label, value, tone }: { label: string; value: string; tone: "ok" | "warn" | "info" }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-line bg-panel2 px-3 py-2">
      <span className="text-slate-400">{label}</span>
      <Badge tone={tone}>{value}</Badge>
    </div>
  );
}

function CounterGrid({ summary }: { summary: RPISOperationalSummary }) {
  const taskPerf = summary.monthly_statistics.task_performance;
  const coord = summary.monthly_statistics.coordination;
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
      <Kpi label="Overdue tasks" value={summary.overdue_tasks.total} tone={summary.overdue_tasks.total ? "bad" : "ok"} />
      <Kpi label="Open alerts" value={summary.open_alerts.total} tone={summary.open_alerts.total ? "warn" : "ok"} />
      <Kpi label="System suggestions" value={summary.system_suggestions.total || 0} tone="info" />
      <Kpi label="Tasks opened" value={taskPerf.tasks_opened} tone="info" hint={summary.month} />
      <Kpi label="Tasks closed" value={taskPerf.tasks_closed} tone="ok" hint={summary.month} />
      <Kpi label="Completed coordination" value={coord.completed_coordination} tone="ok" hint={summary.month} />
      <Kpi label="Waiting external" value={taskPerf.tasks_waiting_external} tone="muted" />
      <Kpi label="External sending performed" value="false" tone="ok" />
    </div>
  );
}

function QuickActions({ canWrite, canProtocol }: { canWrite: boolean; canProtocol: boolean }) {
  return (
    <Card title="Quick links">
      <div className="grid gap-2 text-sm">
        <Link className="btn-ghost justify-center" to="/rpis-manager/due-reminders">View due reminders</Link>
        <Link className="btn-ghost justify-center" to="/tasks">Create task</Link>
        {canWrite ? <a className="btn-ghost justify-center" href="#case-manager-draft">Create case-manager draft</a> : null}
        {canProtocol ? <a className="btn-ghost justify-center" href="#protocol-research">Create protocol research intake</a> : null}
        <Link className="btn-ghost justify-center" to="/reports">Open reports</Link>
      </div>
    </Card>
  );
}

function CaseManagerDraftPanel({ enabled, drafts }: { enabled: boolean; drafts: CaseManagerMessage[] }) {
  const qc = useQueryClient();
  const [patientId, setPatientId] = useState("");
  const [templateKey, setTemplateKey] = useState("custom");
  const [priority, setPriority] = useState("normal");
  const [requiredBy, setRequiredBy] = useState("");
  const [bodyText, setBodyText] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.post(`/rpis-manager/case-manager-drafts?patient_id=${encodeURIComponent(patientId)}`, {
        template_key: templateKey,
        priority,
        required_by: requiredBy || null,
        body_text: bodyText,
        status: "draft",
      }),
    onSuccess: () => {
      setBodyText("");
      qc.invalidateQueries({ queryKey: ["case-manager-drafts"] });
    },
  });

  return (
    <Card title="Case-manager draft" actions={<Badge tone="info">draft only</Badge>}>
      <div id="case-manager-draft" className="space-y-3">
        {!enabled ? <Empty title="Draft creation is limited for this role." /> : (
          <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <input className="input" placeholder="Patient ID" value={patientId} onChange={(e) => setPatientId(e.target.value)} required />
            <div className="grid md:grid-cols-3 gap-3">
              <input className="input" placeholder="Template key" value={templateKey} onChange={(e) => setTemplateKey(e.target.value)} />
              <select className="input" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <input className="input" type="date" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} />
            </div>
            <textarea className="input min-h-28" placeholder="Body text. Leave blank to render the selected template." value={bodyText} onChange={(e) => setBodyText(e.target.value)} />
            {create.error ? <ErrorBox error={create.error} /> : null}
            <button className="btn-primary" disabled={create.isPending}>Create draft</button>
          </form>
        )}
        {drafts.length ? (
          <ul className="space-y-2 text-sm">
            {drafts.map((draft) => (
              <li key={draft.id} className="card-tight">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-100">{draft.template_key}</span>
                  <Badge tone="muted">{draft.priority}</Badge>
                </div>
                <div className="text-xs text-slate-500">patient {draft.patient_id.slice(0, 8)} · {draft.status}</div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}

function ProtocolResearchPanel({
  enabled,
  packets,
  onCreated,
}: {
  enabled: boolean;
  packets: ProtocolResearchPacket[];
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    topic: "",
    disease_site: "",
    clinical_question: "",
    guideline_focus: "",
    urgency: "normal",
    patient_ref: "",
  });
  const create = useMutation({
    mutationFn: () => api.post("/rpis-manager/protocol-research/packets", form),
    onSuccess: onCreated,
  });
  const instructions = useMemo(() => {
    const first = packets.find((p) => p.hermes_instructions)?.hermes_instructions;
    return first || {
      search_internet: true,
      cite_sources: true,
      separate_facts_from_recommendations: true,
      prepare_rpis_task_candidates: true,
      external_sending_allowed: false,
    };
  }, [packets]);

  return (
    <Card title="Protocol research intake" actions={<Badge tone="info">Hermes-ready</Badge>}>
      <div id="protocol-research" className="space-y-3">
        {!enabled ? <Empty title="Protocol intake creation is limited for this role." /> : (
          <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <input className="input" placeholder="Topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} required />
            <div className="grid md:grid-cols-2 gap-3">
              <input className="input" placeholder="Disease site" value={form.disease_site} onChange={(e) => setForm({ ...form, disease_site: e.target.value })} required />
              <select className="input" value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })}>
                {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <input className="input" placeholder="Clinical question" value={form.clinical_question} onChange={(e) => setForm({ ...form, clinical_question: e.target.value })} required />
            <input className="input" placeholder="Guideline focus" value={form.guideline_focus} onChange={(e) => setForm({ ...form, guideline_focus: e.target.value })} required />
            <input className="input" placeholder="Patient reference optional" value={form.patient_ref} onChange={(e) => setForm({ ...form, patient_ref: e.target.value })} />
            {create.error ? <ErrorBox error={create.error} /> : null}
            <button className="btn-primary" disabled={create.isPending}>Create intake packet</button>
          </form>
        )}
        <div className="rounded-md border border-line bg-panel2 p-3 text-xs text-slate-400">
          <pre className="whitespace-pre-wrap">{JSON.stringify(instructions, null, 2)}</pre>
        </div>
        {packets.length ? (
          <ul className="space-y-2 text-sm">
            {packets.slice(0, 6).map((packet) => (
              <li key={packet.file_name} className="card-tight">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-100">{packet.topic || packet.file_name}</span>
                  <Badge tone={packet.urgency === "urgent" ? "bad" : packet.urgency === "high" ? "warn" : "muted"}>
                    {packet.urgency || "normal"}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500">{packet.disease_site || "site pending"} · {packet.status || "intake"}</div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}
